import { authHeaders } from '../auth';
import { useEffect, useRef, useState, useCallback } from 'react';
import AgoraRTC, { IAgoraRTCClient, IAgoraRTCRemoteUser, IRemoteVideoTrack } from 'agora-rtc-sdk-ng';

interface DjiStreamCredentials {
  appId: string;
  channel: string;
  token: string;
  uid: number;
}

interface Detection {
  bbox: [number, number, number, number];
  class: string;
  conf: number;
}

interface DjiStreamPlayerProps {
  credentials: DjiStreamCredentials | null;
  detectionEnabled?: boolean;
  apiUrl?: string;
  onError?: (msg: string) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
}

export default function DjiStreamPlayer({
  credentials, detectionEnabled, apiUrl,
  onError, onConnected, onDisconnected
}: DjiStreamPlayerProps) {
  const videoRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const clientRef = useRef<IAgoraRTCClient | null>(null);
  const videoTrackRef = useRef<IRemoteVideoTrack | null>(null);
  const detectIntervalRef = useRef<number | null>(null);
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [detections, setDetections] = useState<Detection[]>([]);
  const [frameSize, setFrameSize] = useState<{w: number; h: number} | null>(null);

  useEffect(() => {
    if (!credentials) return;

    let client: IAgoraRTCClient;
    let mounted = true;

    const connect = async () => {
      setConnecting(true);
      try {
        client = AgoraRTC.createClient({ mode: 'live', codec: 'h264' });
        client.setClientRole('audience');
        clientRef.current = client;

        client.on('user-published', async (user: IAgoraRTCRemoteUser, mediaType: 'audio' | 'video') => {
          await client.subscribe(user, mediaType);
          if (mediaType === 'video' && videoRef.current) {
            const videoTrack: IRemoteVideoTrack = user.videoTrack!;
            videoTrackRef.current = videoTrack;
            videoTrack.play(videoRef.current);
          }
        });

        client.on('user-unpublished', (user: IAgoraRTCRemoteUser, mediaType: 'audio' | 'video') => {
          if (mediaType === 'video') {
            user.videoTrack?.stop();
            videoTrackRef.current = null;
          }
        });

        client.on('connection-state-change', (curState: string) => {
          if (!mounted) return;
          if (curState === 'DISCONNECTED') {
            setConnected(false);
            onDisconnected?.();
          }
        });

        await client.join(credentials.appId, credentials.channel, credentials.token, credentials.uid);

        if (mounted) {
          setConnected(true);
          setConnecting(false);
          onConnected?.();
        }
      } catch (err: any) {
        if (mounted) {
          setConnecting(false);
          onError?.(err.message || 'Failed to connect to DJI stream');
        }
      }
    };

    connect();

    return () => {
      mounted = false;
      if (clientRef.current) {
        clientRef.current.leave().catch(() => {});
        clientRef.current = null;
      }
      videoTrackRef.current = null;
      setConnected(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentials]);

  const captureAndDetect = useCallback(async () => {
    const track = videoTrackRef.current;
    if (!track || !apiUrl) return;

    try {
      const imageData = track.getCurrentFrameData();
      if (!imageData || imageData.width === 0) return;

      const canvas = document.createElement('canvas');
      canvas.width = imageData.width;
      canvas.height = imageData.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.putImageData(imageData, 0, 0);

      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', 0.7)
      );
      if (!blob) return;

      const formData = new FormData();
      formData.append('file', blob, 'frame.jpg');

      const resp = await fetch(`${apiUrl}/api/drone/detect-frame`, {
        method: 'POST',
        headers: authHeaders(),
        body: formData,
      });
      if (resp.ok) {
        const data = await resp.json();
        setDetections(data.detections || []);
        setFrameSize({ w: data.frame_width, h: data.frame_height });
      }
    } catch {}
  }, [apiUrl]);

  useEffect(() => {
    if (detectionEnabled && connected) {
      detectIntervalRef.current = window.setInterval(captureAndDetect, 500);
    } else {
      if (detectIntervalRef.current) {
        clearInterval(detectIntervalRef.current);
        detectIntervalRef.current = null;
      }
      setDetections([]);
    }
    return () => {
      if (detectIntervalRef.current) {
        clearInterval(detectIntervalRef.current);
      }
    };
  }, [detectionEnabled, connected, captureAndDetect]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !frameSize) return;
    const container = videoRef.current;
    if (!container) return;

    canvas.width = container.clientWidth;
    canvas.height = container.clientHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (detections.length === 0) return;

    const scaleX = canvas.width / frameSize.w;
    const scaleY = canvas.height / frameSize.h;

    for (const det of detections) {
      const [x1, y1, x2, y2] = det.bbox;
      const sx = x1 * scaleX;
      const sy = y1 * scaleY;
      const sw = (x2 - x1) * scaleX;
      const sh = (y2 - y1) * scaleY;

      ctx.strokeStyle = '#00ff00';
      ctx.lineWidth = 2;
      ctx.strokeRect(sx, sy, sw, sh);

      const label = `${det.class} ${det.conf}`;
      ctx.font = '12px monospace';
      const tm = ctx.measureText(label);
      ctx.fillStyle = '#00ff00';
      ctx.fillRect(sx, sy - 16, tm.width + 6, 16);
      ctx.fillStyle = '#000';
      ctx.fillText(label, sx + 3, sy - 4);
    }
  }, [detections, frameSize]);

  if (!credentials) {
    return (
      <div className="dji-stream-placeholder">
        <span>No DJI stream active</span>
      </div>
    );
  }

  return (
    <div className="dji-stream-container" style={{ width: '100%', height: '100%', position: 'relative' }}>
      <div
        ref={videoRef}
        style={{ width: '100%', height: '100%', backgroundColor: '#000' }}
      />
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
          pointerEvents: 'none',
        }}
      />
      {connecting && (
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: 'rgba(0,0,0,0.7)', color: 'var(--color-accent)', fontSize: '14px'
        }}>
          Connecting to DJI stream...
        </div>
      )}
      {connected && (
        <div style={{
          position: 'absolute', top: '8px', left: '8px',
          background: 'rgba(0,0,0,0.6)', padding: '2px 8px',
          borderRadius: '4px', fontSize: '11px', color: 'var(--color-accent)'
        }}>
          ● DJI LIVE {detectionEnabled && detections.length > 0 && `| ${detections.length} detections`}
        </div>
      )}
    </div>
  );
}

export function parseDjiStreamUrl(url: string): DjiStreamCredentials | null {
  try {
    const params = new URLSearchParams(url);
    const appId = params.get('app_id');
    const channel = params.get('channel');
    const token = params.get('token');
    const uid = params.get('uid');
    if (!appId || !channel || !token || !uid) return null;
    return { appId, channel, token, uid: parseInt(uid, 10) };
  } catch {
    return null;
  }
}
